import React, { useEffect } from 'react';
import { Tooltip, IconButton, Box } from '@nimbus-ds/components';
import {
  EditIcon,
  EllipsisIcon,
  LinkIcon,
  QuestionCircleIcon,
  StarIcon,
  TrashIcon,
  InfoCircleIcon,
} from '@nimbus-ds/icons';
import type { StoreDomainDto } from '@tiendanube/common';
import { Popover } from '@tiendanube/components';
import { ModalConfirmationBase } from 'App/components';
import { Stack, useResponsive } from 'commons/components';
import { useModal } from 'commons/hooks';
import { openWindow } from 'commons/utils/window';
import { useCreateDomainMutation } from 'domains/Configurations/Domains/domainsApi';
import { useCustomDomainDisabled } from 'domains/Configurations/Domains/hooks';
import { CREATE_DOMAIN_CACHE_KEY } from 'domains/Configurations/Domains/utils';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import { AttachDomainModal } from '../../AttachDomainModal';
import { DomainInfoModal } from '../../DomainInfoModal';
import { EditOurDomainModal } from '../../EditOurDomainModal';

interface ActionsProps {
  domain: StoreDomainDto;
  ourDomain: string;
  domainsCount: number;
  isDeleting: boolean;
  isUpdating: boolean;
  onDelete: () => void;
  onSetPrimaryDomain: (id: string) => void;
}

export function Actions({
  domain: {
    id,
    name,
    isOurDomain,
    isPrimaryDomain,
    isSubdomain,
    supportsOurDns,
    status,
    isNuvemManaged,
  },
  ourDomain,
  domainsCount,
  isDeleting,
  isUpdating,
  onDelete,
  onSetPrimaryDomain,
}: Readonly<ActionsProps>) {
  const t = useTranslationConfigurations('domains');
  const { isMobile } = useResponsive();
  const [showDeleteModal, openDeleteModal, closeDeleteModal] = useModal();
  const [showEditDomainModal, openEditDomainModal, closeEditDomainModal] =
    useModal();
  const [showAttachDomainModal, openAttachDomainModal, closeAttachDomainModal] =
    useModal();
  const [showInfoModal, openInfoModal, closeInfoModal] = useModal();

  const isFreePlan = useCustomDomainDisabled();
  const showIcons = {
    star:
      !isOurDomain &&
      status.domain === 'ok' &&
      status.ssl === 'ok' &&
      !isPrimaryDomain,
    attach: !isOurDomain && status.domain !== 'ok',
    help: !isOurDomain && status.domain === 'error',
  };

  const [
    ,
    {
      status: createDomainStatus,
      data: createDomainData,
      reset: resetCreateDomain,
    },
  ] = useCreateDomainMutation({
    fixedCacheKey: CREATE_DOMAIN_CACHE_KEY,
  });

  useEffect(() => {
    if (createDomainStatus === 'fulfilled' && createDomainData?.[0].id === id) {
      openAttachDomainModal();
    }
  }, [createDomainStatus, createDomainData, openAttachDomainModal, id]);

  const handleCloseAttachDomainModal = () => {
    closeAttachDomainModal();
    resetCreateDomain();
  };

  const actions = [
    {
      children: t('actions.edit'),
      onClick: openEditDomainModal,
      icon: EditIcon,
      show: isOurDomain && domainsCount === 1,
    },
    {
      children: t('actions.attach'),
      onClick: openAttachDomainModal,
      icon: LinkIcon,
      show: showIcons.attach && !isOurDomain && !isFreePlan,
    },
    {
      children: t('actions.help'),
      onClick: () => openWindow(t('help.error'), true),
      icon: QuestionCircleIcon,
      show: showIcons.help && !isOurDomain,
    },
    {
      children: t('actions.star'),
      onClick: () => onSetPrimaryDomain(id),
      icon: StarIcon,
      show: showIcons.star,
      disabled: isUpdating,
    },
    {
      children: t('actions.delete'),
      onClick: openDeleteModal,
      icon: TrashIcon,
      show: !isOurDomain && !isNuvemManaged,
    },
    {
      children: t('actions.info'),
      onClick: openInfoModal,
      icon: InfoCircleIcon,
      show: isNuvemManaged,
    },
  ];

  if (actions.filter((action) => action.show).length === 0) return null;

  return (
    <Box minHeight="32px">
      <Stack spacing="tight">
        {isMobile ? (
          <Popover
            name="domains-menu"
            menu={actions.filter((option) => option.show)}
            position="right"
            renderInitiator={(onClick) => (
              <IconButton
                size="2rem"
                source={<EllipsisIcon />}
                onClick={onClick}
              />
            )}
          />
        ) : (
          actions.map(
            (action) =>
              action.show && (
                <Tooltip key={action.children} content={action.children}>
                  <IconButton
                    size="2rem"
                    source={React.createElement(action.icon)}
                    onClick={action.onClick}
                    disabled={action.disabled}
                  />
                </Tooltip>
              ),
          )
        )}
      </Stack>
      {isOurDomain ? (
        <EditOurDomainModal
          currentDomain={name}
          show={showEditDomainModal}
          onClose={closeEditDomainModal}
        />
      ) : (
        <>
          <ModalConfirmationBase
            show={showDeleteModal}
            title={t('deleteModal.title')}
            text={t('deleteModal.info')}
            label={t('deleteModal.confirmBtn')}
            labelCancel={t('deleteModal.cancelBtn')}
            onConfirm={onDelete}
            onCancel={closeDeleteModal}
            disabled={isDeleting}
          />
          {showIcons.attach && (
            <AttachDomainModal
              show={showAttachDomainModal}
              domainName={name}
              domainId={id}
              supportsOurDns={supportsOurDns}
              isSubdomain={isSubdomain}
              ourDomain={ourDomain}
              onClose={handleCloseAttachDomainModal}
            />
          )}
        </>
      )}
      <DomainInfoModal
        domainName={name}
        show={showInfoModal}
        onClose={closeInfoModal}
      />
    </Box>
  );
}
